<template>
  <auth-layout>
    <div class="form">
      <div class="form__body">
        <!-- логотип показываем только на экране успешной регистрации:
             в форме он убран, чтобы форма целиком умещалась на экране без прокрутки -->
        <header v-if="isRegistered" class="header">
          <SvgBase width="129" height="35">
            <SvgLogo />
          </SvgBase>
        </header>

        <template v-if="isRegistered">
          <h1 class="text-h6 text-center">Регистрация завершена</h1>
          <p class="text-body-1 text-grey text-center">
            На указанный адрес электронной почты отправлено письмо со ссылкой для завершения
            регистрации: подтверждения e-mail и создания пароля.
          </p>
          <p class="text-body-2 text-grey text-center">
            Если письмо не пришло, проверьте папку «Спам».
          </p>
          <v-btn :block="true" class="registration-submit" color="primary" to="/login">
            Перейти к входу
          </v-btn>
        </template>

        <template v-else>
          <UserForm
            v-model="form"
            v-model:email-server-error="emailServerError"
            submit-label="Зарегистрироваться"
            :loading="isLoading"
            :show-agreement="true"
            @submit="submit"
          />

          <p class="text-body-1 text-grey text-center auth-hint">
            Уже есть учётная запись?
            <router-link to="/login">Войти</router-link>
          </p>
        </template>
      </div>
    </div>
  </auth-layout>
</template>

<script setup>
import { ref } from 'vue'
import { api } from '@/api/Api'
import AuthLayout from '@/layouts/auth'
import SvgBase from '@/components/Svg/SvgBase'
import SvgLogo from '@/components/Svg/SvgLogo.vue'
import UserForm from '@/components/User/UserForm.vue'
import { toIsoBirthDate } from '@/utils/userForm'
import { useToast } from '@/composables/useToast'

const { toast } = useToast()

const form = ref({
  lastname: '',
  firstname: '',
  middlename: '',
  gender: null,
  birthDate: '',
  email: '',
  phone: '',
  region: '',
  city: '',
  organization: '',
  position: '',
  agreement: false,
})

const isLoading = ref(false)
const isRegistered = ref(false)
const emailServerError = ref('')

const submit = async () => {
  emailServerError.value = ''
  isLoading.value = true

  try {
    await api.user().registerUser({
      lastname: form.value.lastname,
      firstname: form.value.firstname,
      middlename: form.value.middlename || null,
      gender: form.value.gender,
      birthDate: toIsoBirthDate(form.value.birthDate),
      email: form.value.email,
      phone: form.value.phone || null,
      region: form.value.region || null,
      city: form.value.city || null,
      organization: form.value.organization || null,
      position: form.value.position || null,
      agreement: form.value.agreement === true,
    })

    isRegistered.value = true
  } catch (err) {
    const status = err?.response?.status

    if (status === 409) {
      emailServerError.value = 'Пользователь с таким e-mail уже зарегистрирован'
      return
    }

    if (status === 400 && err?.response?.data?.error) {
      toast.error(err.response.data.error)
      return
    }

    toast.error('Не удалось завершить регистрацию. Попробуйте позже.')
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
.form {
  width: 550px;
  display: flex;
  flex-direction: column;
  max-width: 1080px;
  justify-content: flex-start;
  align-items: flex-start;

  &__body {
    display: flex;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
    align-items: center;
    justify-content: flex-start;
    padding: 40px 60px;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    gap: 14px;
    width: 100%;
    background: white;

    // безопасное центрирование: когда контент выше карточки,
    // верх не обрезается, а появляется прокрутка
    > :first-child {
      margin-top: auto;
    }

    > :last-child {
      margin-bottom: auto;
    }

    @media (max-width: 599.95px) {
      padding: 24px 16px 45px;
    }
  }

  @media (max-width: 599.95px) {
    width: 100%;
    padding-left: 16px;
    padding-right: 16px;
  }
}

.registration-submit {
  // Vuetify: .v-btn--block задаёт flex: 1 0 auto — в flex-колонке .form__body
  // кнопка «Перейти к входу» растягивалась на всю свободную высоту карточки.
  // flex: none запрещает расти; высоту фиксировали и раньше, но селектор
  // button {} не действовал на <a> (v-btn с to рендерится ссылкой, не кнопкой)
  flex: none;
  height: 52px;
  max-height: 56px;
  margin-top: 12px;
  font-size: 18px;
  border-radius: 20px;
  text-transform: uppercase;
}

.auth-hint {
  a {
    color: var(--primary);
    font-size: inherit;
    text-decoration: underline;
  }
}
</style>
